import React, { useEffect, useRef, useState } from "react";
import {
    Button,
    ButtonBase,
    Checkbox,
    Chip,
    ClickAwayListener,
    Divider,
    Fade,
    FormControlLabel,
    Grid,
    Paper,
    Popper,
    Switch,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import BSInput from "../../../common/js/components/BSInput";
import { DownChevron } from "../../../common/js/icons";
import { each, get, set } from "lodash";
import Scroll from "react-scroll";
import LocationFilters from "../property/LocationFilters";
import searchConstants from "../search/constants";

const StyledStatusContainer = styled(Paper)({
    minWidth: "210px",
    padding: "0 15px",
});

const StyledFakeSelectIcon = styled("span")({
    position: "absolute",
    right: "6px",
    fontSize: "16px",
});

const StyledFilterBar = styled("div")({
    backgroundColor: "white",
    paddingTop: "15px",
    paddingRight: "20px",
    paddingLeft: "15px",
    borderBottom: "1px solid #eee",
});

const StyledStatusSwitch = styled(Switch)({
    marginRight: "-15px",
    color: "red",
});

const StyledMenuTitle = styled("span")({
    fontSize: "14px",
});

const StyledLocationFilterContainer = styled(Paper)({
    minWidth: "450px",
    padding: "20px",
});

const StyledDealTypeFilterContainer = styled(Paper)({
    minWidth: "400px",
    padding: "20px 0px 20px 0px",
    display: "flex",
    justifyContent: "center",
});

const StyledOfficeDropdown = styled(Grid)({
    marginTop: 0,
    height: "30px",
    minWidth: "450px",
});

const StyledDisabledButton = styled(ButtonBase)(({ disabled }) => ({
    height: "39px",
    border: "1px solid #E0E0E0",
    borderRadius: "4px",
    marginLeft: 0,
    fontWeight: 400,
    width: "100%",
    justifyContent: "flex-start",
    padding: "0 26px 0 6px",
    backgroundColor: "white",
    fontSize: "14px",
    "&.Mui-focusVisible": {
        border: "1px solid #2C99D2",
        backgroundColor: "white",
    },
    ...(disabled && { color: "#c4c4c4" }),
}));

const StyledMenuSearchButton = styled(Button)({
    marginBottom: "15px",
});

const StyledFilterOptions = styled("div")({
    flex: "1 1 auto",
});

const StyledFilterContainer = styled("div")({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "1vw",
});

const AuditFilters = (props) => {
    const {
        clearFilters,
        disabled,
        handleDeleteCrumb,
        handleSearchCleared,
        loadedSearchCrumbs,
        searchCriteria,
        searchSubmit,
        updateFilter,
    } = props;

    const clickedOnDropdownToggleRef = useRef(false);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [anchorEl, setAnchorEl] = useState(null);
    const [placement, setPlacement] = useState(null);

    useEffect(() => {
        updateFilter("dealTypeCriteria.kmCompsOnly", true);
    }, [searchCriteria.dealTypeCriteria?.kmCompsOnly]);

    const handleClick = (placementArg) => (event, filter) => {
        clickedOnDropdownToggleRef.current = true;
        const { currentTarget } = event;
        if (openDropdown === filter && placement === placementArg) {
            // Toggle off the same dropdown
            setOpenDropdown(null);
            setAnchorEl(null);
        } else {
            // Open new dropdown
            setOpenDropdown(filter);
            setAnchorEl(currentTarget);
            setPlacement(placementArg);
        }
    };

    const handleClose = () => {
        if (clickedOnDropdownToggleRef.current) {
            clickedOnDropdownToggleRef.current = false; // Reset it
            return; // Skip closing — click was on dropdown toggle
        }
        setOpenDropdown(null);
    };

    const onKeyPress = (event) => {
        if (event.key === "Enter") {
            clickedOnDropdownToggleRef.current = false;
            searchSubmit();
            handleClose();
        }
    };

    const handleClearFilters = () => {
        clearFilters();
        handleSearchCleared(true);
        handleClose();
    };

    const handleSearchSubmit = (searchCriteria) => {
        clickedOnDropdownToggleRef.current = false;
        handleSearchCleared(false);
        searchSubmit(searchCriteria);
        handleClose();
    };

    const countFilter = (searchCriteria, filterName) => {
        let count = 0;
        each(get(searchCriteria, filterName, []), (option) => {
            if (option === true) {
                count += 1;
            }
        });
        return count;
    };

    const checkForLeaseOnly = (checked, box) => {
        let currentState = {
            sale: get(searchCriteria, "dealTypeCriteria.sale"),
            kmCompsOnly: get(searchCriteria, "dealTypeCriteria.kmCompsOnly"),
            lease: get(searchCriteria, "dealTypeCriteria.lease"),
        };
        set(currentState, box, checked);
        updateFilter(`dealTypeCriteria.${box}`, checked);
    };

    let Element = Scroll.Element;

    return (
        <StyledFilterBar>
            <StyledFilterContainer>
                <div style={{ fontWeight: 400, maxWidth: "70px", marginLeft: "12px", marginRight: "5px" }}>
                    Filters:
                </div>
                <StyledFilterOptions>
                    <Popper open={openDropdown === "location"} anchorEl={anchorEl} placement={placement} transition>
                        {({ TransitionProps }) => (
                            <Fade {...TransitionProps} timeout={100}>
                                <div>
                                    <ClickAwayListener onClickAway={handleClose}>
                                        <StyledLocationFilterContainer>
                                            <LocationFilters
                                                updateFilter={updateFilter}
                                                searchCriteria={searchCriteria}
                                                onKeyPress={onKeyPress}
                                            ></LocationFilters>
                                            <Grid container spacing={16} justifyContent="flex-end">
                                                <Grid item>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={(searchCriteria) => handleSearchSubmit(searchCriteria)}
                                                    >
                                                        Search
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </StyledLocationFilterContainer>
                                    </ClickAwayListener>
                                </div>
                            </Fade>
                        )}
                    </Popper>
                    <StyledDisabledButton
                        disabled={disabled}
                        onClick={(e) => handleClick("bottom-start")(e, "location")}
                    >
                        {"Location"}
                        <StyledFakeSelectIcon>
                            <DownChevron />
                        </StyledFakeSelectIcon>
                    </StyledDisabledButton>
                </StyledFilterOptions>
                <StyledFilterOptions>
                    <Popper open={openDropdown === "dealType"} anchorEl={anchorEl} placement={placement} transition>
                        {({ TransitionProps }) => (
                            <Fade {...TransitionProps} timeout={100}>
                                <div>
                                    <ClickAwayListener onClickAway={handleClose}>
                                        <StyledDealTypeFilterContainer>
                                            <Element>
                                                <Grid container style={{ margin: 0 }}>
                                                    <Grid item>
                                                        <Grid
                                                            container
                                                            justifyContent="space-between"
                                                            alignItems="center"
                                                        >
                                                            <Grid item>
                                                                <StyledMenuTitle>KM Comps Only</StyledMenuTitle>
                                                            </Grid>
                                                            <Grid item>
                                                                <StyledStatusSwitch
                                                                    checked={get(
                                                                        searchCriteria,
                                                                        "dealTypeCriteria.kmCompsOnly",
                                                                        true
                                                                    )}
                                                                    disabled={true}
                                                                    onChange={(e) =>
                                                                        checkForLeaseOnly(
                                                                            e.target.checked,
                                                                            "kmCompsOnly"
                                                                        )
                                                                    }
                                                                    value="confidentialComps"
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                        <Grid
                                                            container
                                                            justifyContent="space-between"
                                                            alignItems="center"
                                                        >
                                                            <Grid item>
                                                                <StyledMenuTitle>
                                                                    Include Confidential Comps
                                                                </StyledMenuTitle>
                                                            </Grid>
                                                            <Grid item>
                                                                <StyledStatusSwitch
                                                                    checked={get(
                                                                        searchCriteria,
                                                                        "dealTypeCriteria.confidentialComps",
                                                                        false
                                                                    )}
                                                                    onChange={(e) =>
                                                                        updateFilter(
                                                                            "dealTypeCriteria.confidentialComps",
                                                                            e.target.checked
                                                                        )
                                                                    }
                                                                    value="confidentialComps"
                                                                    color="primary"
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                        <Divider style={{ margin: "10px -20px 12px -20px" }} />
                                                        <Grid container justifyContent="space-between" spacing={8}>
                                                            <Grid
                                                                item
                                                                xs={6}
                                                                style={{ display: "flex", justifyContent: "center" }}
                                                            >
                                                                <FormControlLabel
                                                                    control={
                                                                        <Checkbox
                                                                            checked={get(
                                                                                searchCriteria,
                                                                                "dealTypeCriteria.lease",
                                                                                false
                                                                            )}
                                                                            onChange={(e) =>
                                                                                checkForLeaseOnly(
                                                                                    e.target.checked,
                                                                                    "lease"
                                                                                )
                                                                            }
                                                                            color="primary"
                                                                        />
                                                                    }
                                                                    label="Lease"
                                                                />
                                                            </Grid>
                                                            <Grid
                                                                item
                                                                xs={6}
                                                                style={{ display: "flex", justifyContent: "center" }}
                                                            >
                                                                <FormControlLabel
                                                                    control={
                                                                        <Checkbox
                                                                            checked={get(
                                                                                searchCriteria,
                                                                                "dealTypeCriteria.sale",
                                                                                false
                                                                            )}
                                                                            onChange={(e) =>
                                                                                checkForLeaseOnly(
                                                                                    e.target.checked,
                                                                                    "sale"
                                                                                )
                                                                            }
                                                                            color="primary"
                                                                        />
                                                                    }
                                                                    label="Sale"
                                                                />
                                                            </Grid>
                                                        </Grid>
                                                        <Divider style={{ margin: "10px -20px 24px -20px" }} />
                                                        <Grid
                                                            container
                                                            style={{ display: "flex", flexDirection: "column" }}
                                                        >
                                                            <Grid item>
                                                                <Grid
                                                                    container
                                                                    justifyContent="space-between"
                                                                    spacing={0}
                                                                >
                                                                    <Grid item xs={5}>
                                                                        <BSInput
                                                                            label="Date Signed"
                                                                            placeholder="Date Signed (From)"
                                                                            value={get(
                                                                                searchCriteria,
                                                                                "dealTypeCriteria.transactionDateFrom",
                                                                                ""
                                                                            )}
                                                                            type={"date"}
                                                                            onChange={(e) =>
                                                                                updateFilter(
                                                                                    "dealTypeCriteria.transactionDateFrom",
                                                                                    e.target.value
                                                                                )
                                                                            }
                                                                        />
                                                                    </Grid>
                                                                    <Grid item align="center" xs={1}>
                                                                        <div
                                                                            className="betweenInputs"
                                                                            style={{
                                                                                paddingTop: "30px",
                                                                                paddingLeft: "1vw",
                                                                                paddingRight: "1vw",
                                                                                color: "grey",
                                                                            }}
                                                                        >
                                                                            &mdash;
                                                                        </div>
                                                                    </Grid>
                                                                    <Grid item xs={5}>
                                                                        <BSInput
                                                                            placeholder="Date Signed (To)"
                                                                            value={get(
                                                                                searchCriteria,
                                                                                "dealTypeCriteria.transactionDateTo",
                                                                                ""
                                                                            )}
                                                                            type={"date"}
                                                                            onChange={(e) =>
                                                                                updateFilter(
                                                                                    "dealTypeCriteria.transactionDateTo",
                                                                                    e.target.value
                                                                                )
                                                                            }
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                            <Grid item>
                                                                <Grid
                                                                    container
                                                                    justifyContent="space-between"
                                                                    spacing={0}
                                                                >
                                                                    <Grid item xs={5}>
                                                                        <BSInput
                                                                            label="Transaction Date"
                                                                            placeholder="Transaction Date (From)"
                                                                            value={get(
                                                                                searchCriteria,
                                                                                "dealTypeCriteria.batchDateFrom",
                                                                                ""
                                                                            )}
                                                                            type={"date"}
                                                                            onChange={(e) =>
                                                                                updateFilter(
                                                                                    "dealTypeCriteria.batchDateFrom",
                                                                                    e.target.value
                                                                                )
                                                                            }
                                                                        />
                                                                    </Grid>
                                                                    <Grid item align="center" xs={1}>
                                                                        <div
                                                                            className="betweenInputs"
                                                                            style={{
                                                                                paddingTop: "30px",
                                                                                color: "grey",
                                                                            }}
                                                                        >
                                                                            &mdash;
                                                                        </div>
                                                                    </Grid>
                                                                    <Grid item xs={5}>
                                                                        <BSInput
                                                                            placeholder="Transaction Date (To)"
                                                                            value={get(
                                                                                searchCriteria,
                                                                                "dealTypeCriteria.batchDateTo",
                                                                                ""
                                                                            )}
                                                                            type={"date"}
                                                                            onChange={(e) =>
                                                                                updateFilter(
                                                                                    "dealTypeCriteria.batchDateTo",
                                                                                    e.target.value
                                                                                )
                                                                            }
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                            <Grid item>
                                                                <Grid container justifyContent="flex-end">
                                                                    <Grid item>
                                                                        <StyledMenuSearchButton
                                                                            variant="contained"
                                                                            color="primary"
                                                                            onClick={handleSearchSubmit}
                                                                        >
                                                                            Search
                                                                        </StyledMenuSearchButton>
                                                                    </Grid>
                                                                </Grid>
                                                            </Grid>
                                                        </Grid>
                                                    </Grid>
                                                </Grid>
                                            </Element>
                                        </StyledDealTypeFilterContainer>
                                    </ClickAwayListener>
                                </div>
                            </Fade>
                        )}
                    </Popper>
                    <StyledDisabledButton
                        disabled={disabled}
                        onClick={(e) => handleClick("bottom-start")(e, "dealType")}
                    >
                        {`Deal Type ${
                            countFilter(searchCriteria, "dealTypeCriteria") > 0
                                ? `(${countFilter(searchCriteria, "dealTypeCriteria")})`
                                : ""
                        }`}
                        <StyledFakeSelectIcon>
                            <DownChevron />
                        </StyledFakeSelectIcon>
                    </StyledDisabledButton>
                </StyledFilterOptions>

                <StyledFilterOptions>
                    <Popper open={openDropdown === "kmOffice"} anchorEl={anchorEl} placement={placement} transition>
                        {({ TransitionProps }) => (
                            <Fade {...TransitionProps} timeout={100}>
                                <div>
                                    <ClickAwayListener onClickAway={handleClose}>
                                        <StyledStatusContainer>
                                            <Element style={{ height: "400px", overflowY: "scroll" }}>
                                                <Grid container direction="column">
                                                    {/*for each office*/}
                                                    {Object.keys(searchConstants.kmOffices).map((key, i) => {
                                                        const option = searchConstants.kmOffices[key];
                                                        return (
                                                            <StyledOfficeDropdown item>
                                                                <FormControlLabel
                                                                    control={
                                                                        <Checkbox
                                                                            checked={get(
                                                                                searchCriteria,
                                                                                `officeCriteria.${key}`,
                                                                                false
                                                                            )}
                                                                            onChange={(e) =>
                                                                                updateFilter(
                                                                                    "officeCriteria." + key,
                                                                                    e.target.checked
                                                                                )
                                                                            }
                                                                            color="primary"
                                                                        />
                                                                    }
                                                                    label={option.display}
                                                                />
                                                            </StyledOfficeDropdown>
                                                        );
                                                    })}
                                                </Grid>
                                            </Element>
                                            <Grid container spacing={16} justifyContent="flex-end">
                                                <Grid item style={{ paddingTop: "30px", paddingBottom: "30px" }}>
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        onClick={handleSearchSubmit}
                                                    >
                                                        Search
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </StyledStatusContainer>
                                    </ClickAwayListener>
                                </div>
                            </Fade>
                        )}
                    </Popper>
                    <StyledDisabledButton
                        disabled={disabled}
                        onClick={(e) => handleClick("bottom-start")(e, "kmOffice")}
                    >
                        {`KM Office ${
                            countFilter(searchCriteria, "officeCriteria") > 0
                                ? `(${countFilter(searchCriteria, "officeCriteria")})`
                                : ""
                        }`}
                        <StyledFakeSelectIcon>
                            <DownChevron />
                        </StyledFakeSelectIcon>
                    </StyledDisabledButton>
                </StyledFilterOptions>
                <div>
                    <Button variant="outlined" onClick={handleClearFilters}>
                        Clear
                    </Button>
                </div>
            </StyledFilterContainer>
            <Grid
                container
                justifyContent="space-between"
                alignItems="center"
                spacing={8}
                style={{ marginTop: "10px" }}
            >
                <Grid item>
                    {loadedSearchCrumbs
                        ? Object.keys(loadedSearchCrumbs).map((criteria) => {
                              if (loadedSearchCrumbs[criteria].value === true) {
                                  if (Object.keys(loadedSearchCrumbs[criteria]).length === 2) {
                                      return (
                                          <Chip
                                              style={{ marginLeft: "10px", marginBottom: "10px" }}
                                              color="primary"
                                              label={criteria}
                                              onDelete={() => handleDeleteCrumb(loadedSearchCrumbs[criteria], false)}
                                          />
                                      );
                                  } else if (
                                      (loadedSearchCrumbs[criteria].valueMin !== "* " &&
                                          loadedSearchCrumbs[criteria].valueMin !== undefined) ||
                                      (loadedSearchCrumbs[criteria].valueMax !== " *" &&
                                          loadedSearchCrumbs[criteria].valueMax !== undefined)
                                  ) {
                                      return (
                                          <Chip
                                              style={{ marginLeft: "10px", marginBottom: "10px" }}
                                              color="primary"
                                              label={
                                                  criteria +
                                                  get(loadedSearchCrumbs[criteria], "unit", "") +
                                                  ": " +
                                                  get(loadedSearchCrumbs[criteria], "valueMin", "* ") +
                                                  "-" +
                                                  get(loadedSearchCrumbs[criteria], "valueMax", " *")
                                              }
                                              onDelete={() => handleDeleteCrumb(loadedSearchCrumbs[criteria], null)}
                                          />
                                      );
                                  }
                              } else if (
                                  typeof loadedSearchCrumbs[criteria].value == "string" &&
                                  loadedSearchCrumbs[criteria].value != null
                              ) {
                                  return (
                                      <Chip
                                          style={{ marginLeft: "10px", marginBottom: "10px" }}
                                          color="primary"
                                          label={criteria + ": " + loadedSearchCrumbs[criteria].value}
                                          onDelete={() => handleDeleteCrumb(loadedSearchCrumbs[criteria], null)}
                                      />
                                  );
                              }
                              return null;
                          })
                        : ""}
                </Grid>
            </Grid>
        </StyledFilterBar>
    );
};

export default AuditFilters;
